import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { Card } from '@shared/ui/containers/Card';
import { Button } from '@shared/ui/primitives/Button';
import { cn } from '@shared/lib/cn';
import { formatDate, formatMoney } from '@shared/lib/format';
import { pageCopy } from '@shared/lib/pageCopy';
import { platform, publicBaseUrl } from '@shared/lib/platform';
import { openLink } from '@shared/lib/openLink';
import { useModuleStore } from '@shared/stores/moduleStore';
import { toast } from '@shared/ui/feedback/Toast';
import {
  fetchAgeingSummary,
  payAccountBalance,
  type BillingAccount,
  type MemberAgeing,
} from './api';
import { deriveBalanceState, type BalanceState, type BalanceStatus } from './balanceState';

/**
 * The member's arrears ageing, when the statements license is on. One shared
 * query key, so the hero and the Account page's ageing card cost a single
 * fetch — and the home widget gets the same answer as the page.
 */
export function useAccountAgeing(): MemberAgeing | null | undefined {
  const enabled = useModuleStore((s) => s.isModuleEnabled('financials:statements'));
  return useQuery({ queryKey: ['member-ageing'], queryFn: fetchAgeingSummary, enabled }).data;
}

/**
 * Tints. Tailwind's `/opacity` modifier is a no-op on this app's colour tokens
 * (they are bare `var(--…)` values — see the note in styles/globals.css), so
 * every tint goes through `color-mix` on the same tokens.
 */
const PILL_TONE: Record<BalanceStatus, string> = {
  settled: 'bg-[color-mix(in_oklch,var(--success)_14%,transparent)] text-success',
  owing: 'bg-[color-mix(in_oklch,var(--warning)_20%,transparent)] text-warning',
  overdue: 'bg-[color-mix(in_oklch,var(--danger)_14%,transparent)] text-danger',
  credit: 'bg-[color-mix(in_oklch,var(--accent-forest)_18%,transparent)] text-forest',
};

const AMOUNT_TONE: Record<BalanceStatus, string> = {
  settled: 'text-ink',
  owing: 'text-ink',
  overdue: 'text-danger',
  credit: 'text-success',
};

function pillLabel(state: BalanceState): string {
  switch (state.status) {
    case 'settled':
      return 'Settled';
    case 'owing':
      return 'Payment due';
    case 'overdue':
      return `${state.daysLate}+ days late`;
    case 'credit':
      return 'Credit';
  }
}

/**
 * The account hero: eyebrow label, unsigned amount, status pill, hairline, and
 * a state-driven footer slot. The card never changes shape between states —
 * only the label, the amount colour, the pill and the footer swap.
 */
export function BalanceCard({
  account,
  canPay,
}: {
  account: BillingAccount;
  /** True only when the tenant has a default payment channel + an amount is due. */
  canPay: boolean;
}): JSX.Element {
  const state = deriveBalanceState(account, useAccountAgeing());

  return (
    <Card
      noGrain
      className={cn(
        'rounded-lg p-5',
        state.status === 'overdue' &&
          'border-[color-mix(in_oklch,var(--danger)_45%,transparent)]',
      )}
    >
      <BalanceSummary state={state} account={account} canPay={canPay} />
    </Card>
  );
}

/**
 * The hero's contents without the card, so the home dashboard can seat it in
 * its own widget card (features/dashboard/widgets/AccountWidget).
 */
export function BalanceSummary({
  state,
  account,
  canPay,
}: {
  state: BalanceState;
  account: BillingAccount;
  canPay: boolean;
}): JSX.Element {
  return (
    <div>
      {/* 1. Direction label + severity pill. The label is what keeps the amount unsigned. */}
      <div className="flex items-start justify-between gap-3">
        <span className="eyebrow">{state.label}</span>
        <span
          className={cn(
            'shrink-0 rounded-pill px-2.5 py-1 text-[0.8125rem] font-medium',
            PILL_TONE[state.status],
          )}
        >
          {pillLabel(state)}
        </span>
      </div>

      {/* 2. The amount, in the display face. Screen readers pick up the label above it. */}
      <p className={cn('mt-2 font-display text-display-2xl tabular leading-none', AMOUNT_TONE[state.status])}>
        {formatMoney(state.amount, { currency: account.currency_code })}
      </p>

      {/* 3. Footer slot — the only part of the card that varies by state. */}
      <div className="mt-5 hairline-t pt-4">
        <BalanceFooter state={state} account={account} canPay={canPay} />
      </div>
    </div>
  );
}

/** The quiet line under the hairline. Never empty, so the divider always has a job. */
function footerNote(status: BalanceStatus, canPay: boolean): string | null {
  switch (status) {
    case 'settled':
      return 'You are fully paid up.';
    case 'credit':
      return 'Goes towards your next invoice.';
    case 'overdue':
      return 'Interest may apply while this balance is outstanding.';
    case 'owing':
      return canPay ? null : 'Contact the estate office to settle this balance.';
  }
}

function BalanceFooter({
  state,
  account,
  canPay,
}: {
  state: BalanceState;
  account: BillingAccount;
  canPay: boolean;
}): JSX.Element {
  const copy = pageCopy().invoices;
  const [paying, setPaying] = useState(false);
  const showPay = canPay && (state.status === 'owing' || state.status === 'overdue');
  const note = footerNote(state.status, canPay);

  async function pay() {
    setPaying(true);
    try {
      // Native runs under a custom scheme the gateway can't return to, so it returns to the
      // public payment-complete page instead.
      const returnUrl = platform.isNative() ? `${publicBaseUrl()}/payment/complete` : window.location.href;
      const url = await payAccountBalance(returnUrl);
      if (!url) return;
      if (platform.isNative()) {
        await openLink(url);
      } else {
        window.location.href = url;
      }
    } catch (error) {
      toast.danger("Couldn't start payment", error instanceof Error ? error.message : 'Please try again.');
    } finally {
      setPaying(false);
    }
  }

  return (
    <>
      {showPay ? (
        <Button
          width="full"
          variant={state.status === 'overdue' ? 'danger' : 'primary'}
          className={cn(state.status !== 'overdue' && 'bg-primary-900 hover:bg-primary-800')}
          loading={paying}
          aria-label={`${copy.payCta} ${formatMoney(state.amount, { currency: account.currency_code })}`}
          onClick={(e) => {
            // The home widget wraps this card in a tap-to-open target.
            e.stopPropagation();
            pay();
          }}
        >
          {copy.payCta}
          <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </Button>
      ) : null}

      {note ? (
        <p className={cn('text-body-sm text-ink-secondary', showPay && 'mt-3')}>{note}</p>
      ) : null}

      {state.status === 'settled' && account.updated_at ? (
        <p className="mt-1 text-caption text-ink-muted">Updated {formatDate(account.updated_at)}</p>
      ) : null}
    </>
  );
}
